import React, { useState, useEffect, useRef } from "react";
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import {
  Input,
  FormControl,
  FormLabel,
  Box,
  Textarea,
} from "@chakra-ui/react";
import ReactSelect from "react-select";
// Date picker
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale } from  "react-datepicker";
import es from 'date-fns/locale/es';
// Main components
import MainLayout from "../../../components/main/layout/layout";
// Common components
import CommonCheckbox from "../../../components/common/checkbox/checkbox";
// Maintenance components
import MaintenanceForm from "../../../components/maintenance/form/form";
// Components
import Upload from "../../../components/upload/upload";
// Actions
import * as mainActions from "../../../redux/actions/main";
import * as podcastsActions from "../../../redux/actions/podcasts";
import * as podcastsGroupsActions from "../../../redux/actions/podcastsGroups";
import * as filesActions from "../../../redux/actions/files";
// Styles
import "./form.css";

// Vars
registerLocale('es', es)

const PodcastsForm = ({ isNew }) => {
  const dispatch = useDispatch();
  const childRef = useRef();
  // States
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState({
    "enabled": true,
  });
  const [isDirty, setIsDirty] = useState(false);
  const [isDirtyFiles, setIsDirtyFiles] = useState(false);
  const [files_1, setFiles_1] = useState([]);
  const [files_2, setFiles_2] = useState([]);
  // Selectors
  const currentPage = useSelector((state) => state.mainReducer.currentPage);
  const groupsData = useSelector((state) => state.podcastsGroupsReducer.data);  

  // Set constants
  const page = "podcasts";
  const primaryName = !isNew && !!form[`title`] ? form[`title`] : ``;
  const itemNameSingular = "Podcast";

  // Get id from url
  let id = null;
  const { id: paramId } = useParams();
  if (!isNew) {
    id = parseInt(paramId);
  }

  useEffect(() => {

    // Get groups data
    if (groupsData.length === 0) {
      dispatch(podcastsGroupsActions.getData());
    }

    if (!!currentPage) return;

    // Set active page
    dispatch(mainActions.setCurrentPage(page));

    // Set initial breadscrum
    if (isNew) {
      const pathKey = 'new';
      const path = `${page}/${pathKey}`;
      dispatch(mainActions.setBreadscrum(page, [{ path, pathKey: 'new-m', itemNameSingular, }]));
    } else {
      const pathKey = 'edit';
      const path = `${page}/${pathKey}/${id}`;
      dispatch(mainActions.setBreadscrum(page, [{ path, pathKey, primaryValue: id, primaryName, itemNameSingular, }]));
    }
  }, []);

  const handleChange = (evt) => {
    if (childRef.current) {
      childRef.current.handleChange(evt);
    }
  };

  const renderForm = () => {
    return (
      <div className="podcasts-form-wrapper">
        <div className="podcasts-form-firstcolumn-wrapper">
          {renderMainCard()}
        </div>
        <div className="maintenance-form-card">
          {renderUploadCard_1()}
          {renderUploadCard_2()}
        </div>
      </div>
    );
  };

  const renderMainCard = () => {
    let title;
    if (isNew) {
      title = "Nou Podcast";
    } else {
      title = `Podcast${!!form[`id`] ? ` amb Id: ${form[`id`]}` : ''}`;
    }

    // Set options for combo
    const options = [];
    for (var i = 0; i < groupsData.length; i++) { 
      const group = groupsData[i];
      options.push({
        value: group.id,
        label: group.title,
      })
    }

    return (
      <div className="podcasts-form-maincard">
        <div className="title maintenance-form-title">
          {title}
        </div>  
        {/* IMPORTANT !!! Els checkboxes no poden estar en el mateix FormControl En producció no funciona!! */}
        <CommonCheckbox
          isChecked={!!form['enabled']}
          onChange={(e) => {
            setForm({
              ...form,
              "enabled": e.target.checked,
            });     
            setIsDirty(true);         
          }}
        >
          Habilitat
        </CommonCheckbox>            
        <FormControl isRequired mt={4}>
          <FormLabel fontSize="sm" className="maintenance-form-label">
            Títol
          </FormLabel>
          <Input
            className="maintenance-form-field"
            placeholder="Títol"
            name="title"
            data-type={`string`}
            onChange={handleChange}
            value={form[`title`] ? form[`title`] : ``}
          />
        </FormControl>
        <FormControl mt={4}>
          <FormLabel fontSize="sm" className="maintenance-form-label">
            Descripció
          </FormLabel>
          <Textarea
            className="maintenance-form-field"
            placeholder="Descripció"
            name="description"
            data-type={`string`}
            onChange={handleChange}
            value={form[`description`] ? form[`description`] : ``}
            minHeight="200px"
          />
        </FormControl>        
        <FormControl isRequired mt={4}>
          <FormLabel fontSize="sm" className='maintenance-form-label'>
              Grup
          </FormLabel>
          <ReactSelect
            className="maintenance-form-combo"
            classNamePrefix="react-select"
            name="podcasts_group_id"
            options={options}
            isSearchable
            value={options.find(opt => opt.value === form["podcasts_group_id"]) || null}
            onChange={(selectedOption) => {
                setForm(prev => ({
                    ...prev,
                    podcasts_group_id: selectedOption ? selectedOption.value : null
                }));
                setIsDirty(true);
            }}
            placeholder="Grup"
            required
          />
        </FormControl>
        <Box 
          display="flex" 
          flexDirection="row" 
          gap={4}
        >      
          <FormControl mt={4} isRequired w="150px" >
            <FormLabel fontSize="sm" className="maintenance-form-label">
              Data
            </FormLabel>
            <DatePicker 
                locale="es"
                dateFormat="dd/MM/yy"             
                selected={!!form[`date`] ? new Date(form[`date`]) : ''} 
                // onSelect={(date) => {                           
                // }} //when day is clicked
                onChange={(date) => { 
                    handleChange({
                      isDate: true, 
                      onlyDate: true,
                      fieldName: 'date', 
                      value: date,
                    })            
                }} 
                customInput={
                  <Input
                    className="maintenance-form-field"
                    textAlign="center"
                  />
                }
                placeholderText="Data" 
                className="custom-datepicker"
                portalId="root-portal" // Esto forzará el renderizado en el portal
                popperPlacement="bottom-start" // Ajusta la posición del calendario
                disabled={false}
            />
          </FormControl>
          <FormControl w="200px" mt={4} >
            <FormLabel fontSize="sm" className="maintenance-form-label">
              Prioritat
            </FormLabel>
            <Input
                className="maintenance-form-field"     
                placeholder="Prioritat"
                name={`priority`}
                data-type={`integer`}
                onChange={(e) => {
                  const inputValue = e.target.value;
                  if (!isNaN(inputValue)) {
                    handleChange(e);
                  }
                }}
                value={form[`priority`] ? form[`priority`] : ``}
                inputMode="numeric"
                w="120px"
                textAlign="left"
                type="number"
                min={1}
            />
          </FormControl>
        </Box>
        <FormControl mt={4}>
          <FormLabel fontSize="sm" className="maintenance-form-label">
            URL web
          </FormLabel>
          <Input
            className="maintenance-form-field"
            placeholder="https://"
            name="url_web"
            data-type={`string`}
            type="url"
            onChange={handleChange}
            value={form[`url_web`] ? form[`url_web`] : ``}
            maxLength={1024}
          />       
        </FormControl>         
      </div>   
    );
  };

  const renderUploadCard_1 = () => {
    return (
      <div className="podcasts-form-upload-card">
        <FormControl isRequired>
          <FormLabel fontSize="sm" className="maintenance-form-label">
            Títol de l'àudio
          </FormLabel>
          <Input
            className="maintenance-form-field"
            placeholder="Títol de l'audio"
            name="text_audio"
            data-type={`string`}
            onChange={handleChange}
            value={form[`text_audio`] ? form[`text_audio`] : ``}
          />
        </FormControl>        
        <FormControl mt={4}>
          <FormLabel fontSize="sm" className="maintenance-form-label">
            Fitxer d'audio
          </FormLabel>    
          {/* <Input
            className="maintenance-form-field"
            placeholder="https://"
            name="url_audio_1"
            data-type={`string`}
            type="url"
            onChange={handleChange}
            value={form[`url_audio_1`] ? form[`url_audio_1`] : ``}
          /> */}
        </FormControl>       
        <Upload 
          type={"podcasts"}
          id={id}
          id2={"1"}
          style={{ marginTop: "10px"}}
          onChange={(files) => {
            setFiles_1(files);        
          }}      
          onNewFile={(newFiles) => {
            setIsDirty(true);
            setIsDirtyFiles(true);
          }}           
          onDeleteFile={(file) => {
            setIsDirty(true);
            setIsDirtyFiles(true);
          }}           
          onUpdateFile={(file) => {
            setIsDirty(true);
            setIsDirtyFiles(true);
          }} 
          showTextFile={false}
        />
      </div>
    );
  };
  
    const renderUploadCard_2 = () => {
      return (
        <div className="live-form-upload-card">
          <FormControl>
            <FormLabel fontSize="sm" className="maintenance-form-label">
              Imatge
            </FormLabel>    
            {/* <Input
              className="maintenance-form-field"
              placeholder="https://"
              name="url_image_1"
              data-type={`string`}
              type="url"
              onChange={handleChange}
              value={form[`url_image_1`] ? form[`url_image_1`] : ``}
            /> */}
          </FormControl>              
          <Upload 
            type={"podcasts"}
            id={id}
            id2={"2"}
            style={{ marginTop: "10px"}}
            onChange={(files) => {
              setFiles_2(files);        
            }}      
            onNewFile={(newFiles) => {
              setIsDirty(true);
              setIsDirtyFiles(true);
            }}           
            onDeleteFile={(file) => {
              setIsDirty(true);
              setIsDirtyFiles(true);
            }}           
            onUpdateFile={(file) => {
              setIsDirty(true);
              setIsDirtyFiles(true);
            }} 
          />
        </div>
      );
    };

  return (
    <MainLayout isLoading={isLoading} setIsLoading={setIsLoading}>
      <div id="podcasts-form-content">
        <MaintenanceForm 
          primaryKey={"id"}
          primaryValue={id}
          primaryName={primaryName}
          config={{
            submitFields: [ 
              `title`,
              `description`,
              `date`,
              `priority`,
              `url_audio_1`,
              `url_image_1`,
              `podcasts_group_id`,
              `text_audio`,
              `url_web`,
              `enabled`,
            ],
          }}
          menuConfig={{}}
          itemNameSingular={itemNameSingular}
          itemIsMale={true}
          actions={podcastsActions} 
          setIsLoading={setIsLoading}
          renderForm={renderForm}
          form={form}
          setForm={setForm}
          isDirty={isDirty}
          setIsDirty={setIsDirty}
          ref={childRef}
          isNew={isNew}
          page={"podcasts"}
          executeAfterSaving={async (theId) => {
            return new Promise((resolve) => {
              if (!isDirtyFiles) {
                resolve({ success: true });
              }
              dispatch(filesActions.setFiles("podcasts", theId, "1", files_1)).then(
                () => {
                  dispatch(filesActions.setFiles("podcasts", theId, "2", files_2)).then(
                    () => {
                      resolve({ success: true });
                    },
                    (err) => resolve({ success: false, msg: err })
                  );
                },
                (err) => resolve({ success: false, msg: err })
              );
            });
          }}
        />
      </div>
    </MainLayout>
  );
};

export default PodcastsForm;
